import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppUIStore } from "@/domains/app/stores/app-ui-store";
import { useMobileAppLayout } from "@/lib/providers/mobile-app-layout-context";
import { useSidebarStore } from "@/lib/store/sidebar";
import { cn } from "@/lib/utils";
import type { NavigationConfig } from "./types";

const DEFAULT_TRAILING_ELEMENTS: React.ReactNode[] = [];

export function BaseNavigationBarDesktop({
  title,
  subtitle,
  backButton,
  leadElement,
  trailingElements = DEFAULT_TRAILING_ELEMENTS,
  trailElement,
  bottomElement,
}: NavigationConfig) {
  // Use unified scroll detection from context (same as mobile)
  const { isScrolled, setNavBarHeight } = useMobileAppLayout();
  // Read sidebar open state directly from the centralized zustand store and
  // derive the same "state" string the rest of the layout expects.
  const state = useSidebarStore((s) => (s.open ? "expanded" : "collapsed"));
  const navRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Get right sidebar state for dynamic padding
  const rightSidebarOpen = useAppUIStore((state) => state.rightSidebarOpen);

  // Check if we're in the settings area
  const isInSettings = location.pathname.startsWith("/settings");

  // Calculate left position based on sidebar state
  const getLeftPosition = () => {
    if (isInSettings) {
      // Settings pages have wider sidebar area — use larger offsets so nav doesn't overlap
      return state === "collapsed" ? "345px" : "561px";
    } else {
      // Normal pages (w-70 = 280px + 8px padding = 288px, rounded to 285px to match main)
      return state === "collapsed" ? "66px" : "285px";
    }
  };

  // Combine trailElement and trailingElements for backward compatibility
  const allTrailingElements = [...(trailElement ? [trailElement] : []), ...trailingElements];

  // Measure and report nav bar height (like mobile does)
  useEffect(() => {
    const measureHeight = () => {
      if (navRef.current) {
        const height = navRef.current.offsetHeight;
        setNavBarHeight(height);
      }
    };

    // Measure on mount and when content changes
    measureHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(measureHeight);
    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setNavBarHeight]);
  return (
    <div
      ref={navRef}
      className={cn(
        "base-nav fixed pt-2 px-2 z-30 transition-all duration-200 ease-in-out rounded-lg",
        isScrolled && ["bg-background/20 backdrop-blur-md", "border border-border", "shadow-lg"]
      )}
      style={{
        // top-2 (0.5rem), pushed down by the global paused-bookings banner when shown.
        top: "calc(0.5rem + var(--paused-banner-height, 0px))",
        left: getLeftPosition(),
        right: rightSidebarOpen ? "456px" : "8px", // 440px sidebar + 8px padding on each side
      }}
    >
      {/* Back Button (above title) */}
      {backButton && <div className="px-3">{backButton}</div>}

      {/* Header with Title and Actions */}
      <div className="pt-safe-top flex items-center justify-between gap-4 mb-2 px-2">
        <div className="flex items-center gap-4 flex-1">
          {/* Title and Subtitle */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground mb-1">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>

          {/* Lead Element */}
          {leadElement && <div className="flex items-center gap-2">{leadElement}</div>}
        </div>

        <div className="flex items-center gap-3">
          {/* Trailing Elements */}
          {allTrailingElements.map((element, index) => (
            <div key={index}>{element}</div>
          ))}
        </div>
      </div>

      {/* Bottom Element */}
      {bottomElement && <div className="mb-3 px-2">{bottomElement}</div>}
    </div>
  );
}
